import {
  TranscriptTermCorrectionError,
  type TranscriptTermCorrectionResult,
  useStores,
} from '@littlebird/sdk';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

import { FEATURES, useFeature } from '@/features/feature-flags';
import { setHashInElectron } from '@/hooks/useHashRoute';
import { userStore } from '@/stores/user.store';

import { meetingStore } from './meetings.state';

import type { TranscriptWordSelection } from './meetings-transcript-display.utils';

export interface TranscriptTermCorrectionController {
  isEnabled: boolean;
  selection: TranscriptWordSelection | null;
  isSaving: boolean;
  selectWord: (selection: TranscriptWordSelection) => void;
  cancel: () => void;
  save: (correctedTerm: string) => void;
}

type CorrectionSelection = {
  meetingId: string;
  transcript: string;
  word: TranscriptWordSelection;
};

function notifyTranscriptCorrected(result: TranscriptTermCorrectionResult) {
  if (result.vocabularyLimitReached) {
    toast.warning('Transcript corrected, but your vocabulary is full', {
      description: 'Remove a term in settings to add another.',
      action: {
        label: 'Open settings',
        onClick: () =>
          setHashInElectron('settings/Meetings/meetings.custom-vocabulary'),
      },
    });
    return;
  }

  const description =
    result.searchRefreshStatus === 'pending'
      ? 'Search is updating automatically.'
      : undefined;

  toast.success('Transcript corrected', { description });
}

export function useTranscriptTermCorrection({
  meetingId,
  transcript,
  canCorrect,
}: {
  meetingId: string;
  transcript: string;
  canCorrect: boolean;
}): TranscriptTermCorrectionController {
  const { meetings } = useStores();
  const hasFeature = useFeature();
  const isEnabled =
    canCorrect && hasFeature(FEATURES.MEETING_NOTES_CUSTOM_VOCABULARY);

  const [selection, setSelection] = useState<CorrectionSelection | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const saving = useRef(false);

  // A correction belongs to the text the user selected, not a later sync
  const current =
    isEnabled &&
    selection?.meetingId === meetingId &&
    selection.transcript === transcript
      ? selection
      : null;

  if (selection !== current) setSelection(current);

  const clearSelection = (savedSelection: CorrectionSelection) => {
    setSelection((previous) => {
      if (previous !== savedSelection) return previous;
      return null;
    });
  };

  const save = async (correctedTerm: string) => {
    if (!current || saving.current) return;

    const replacement = correctedTerm.trim();
    if (!replacement || replacement === current.word.term) return;

    saving.current = true;
    setIsSaving(true);
    const userId = userStore.user?.id;

    try {
      const result = await meetings.correctTranscriptTerm(
        meetingId,
        current.transcript,
        current.word.term,
        replacement
      );

      if (userStore.user?.id !== userId) return;

      meetingStore.setCommittedTranscript(
        meetingId,
        result.personifiedTranscript
      );
      userStore.updateUser({
        custom_transcription_vocabulary: result.customTranscriptionVocabulary,
      });

      clearSelection(current);
      notifyTranscriptCorrected(result);
    } catch (error) {
      if (userStore.user?.id !== userId) return;

      console.error('Failed to correct transcript term', error);

      const latestTranscript = meetings.get(meetingId)?.personifiedTranscript;
      if (latestTranscript !== undefined) {
        meetingStore.setCommittedTranscript(meetingId, latestTranscript ?? '');
      }

      const correctionError = TranscriptTermCorrectionError.from(error);
      if (correctionError.requiresReselection) {
        clearSelection(current);
      }

      toast.error(correctionError.displayMessage, {
        description: correctionError.displayDescription,
      });
    } finally {
      saving.current = false;
      setIsSaving(false);
    }
  };

  return {
    isEnabled,
    selection: current?.word ?? null,
    isSaving,
    selectWord: (word) => {
      if (isEnabled && !saving.current) {
        setSelection({ meetingId, transcript, word });
      }
    },
    cancel: () => {
      if (!saving.current) setSelection(null);
    },
    save,
  };
}
